import { ContextStore, ExperimentStore } from 'bernie-plugin-mobx';
import { inject } from 'mobx-react';
import * as React from 'react';

import { PropertyListing } from '@shared-ui/lodging-property-listing';

import { CurrentHotelStore } from 'src/common/stores/current-hotel-store';
import { getPropertySearchInput } from 'stores/adapters';
import { MultiItemStore } from 'stores/multi-item-store';
import { SearchResultsStore } from 'stores/search-results-store';
import { UIPageStore } from 'stores/ui-page-store';

import { PropertySearchListingViewModel } from 'models';
import { ResultType } from 'constants/resultType';

import { LodgingPwaPropertySearch } from '__generated__/api';

export interface PropertyListingWrapperProps {
  context?: ContextStore;
  multiItem?: MultiItemStore;
  searchResults?: SearchResultsStore;
  experiment?: ExperimentStore;
  uiPage?: UIPageStore;
  currentHotel?: CurrentHotelStore;
}

export const PropertyListingWrapper = inject(
  'context',
  'multiItem',
  'searchResults',
  'experiment',
  'uiPage',
  'currentHotel'
)(({ context, multiItem, searchResults, experiment, uiPage, currentHotel }: PropertyListingWrapperProps) => {
  const multiItemContext = multiItem ? multiItem.multiItemContext : null;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { context: bexApiContext, shouldFetchSortAndFilter, returnPropertyType, ...rest } = getPropertySearchInput(
    context,
    searchResults?.searchCriteria,
    multiItemContext,
    false,
    null,
    experiment,
    searchResults?.pageData?.pageId
  );

  const propertyListingInput = {
    ...rest,
    rooms: searchResults?.searchCriteria?.rooms,
  };

  const openDatepicker = () => {
    uiPage?.dispatchOpenNewDatePickerEvent();
  };

  /**
   * To be removed once the Property Search query is migrated out of shopping-pwa
   *
   * Some cards like free_cancellation and loyalty messages are duplicated as Messaging type cards and their new shared UI respective types.
   * This functions filters out those old type cards from the propertyResults list.
   */
  const isDuplicateCard = (listing: PropertySearchListingViewModel) => {
    // Remove FREE_CANCELLATION card since it now comes as a EGDSStandardMessagingCard
    if (listing.resultType === ResultType.FREE_CANCELLATION) return false;

    // Remove LOYALTY_CARD card since it now comes as a PropertyListingsIconTextExpando
    if (listing.resultType === ResultType.MESSAGING) {
      const model = listing.model as LodgingPwaPropertySearch.MessageResultInlineFragment;

      /**
       * Result cards which "content" field is populated are rendered as an ExpandoCard
       * In order to avoid duplication with the new SharedUI PropertyListingExpandoCardWrapper component
       * this scenario is now filtered out. This logic is to be removed once the PropertyListing component
       * is fully tested.
       */
      if (model.content?.length) {
        return false;
      }
    }

    return true;
  };

  // I hate using "any" but the search listing types are so different between PWA and Shared UI that we kinda have to for now.
  const searchListings = searchResults.propertySearchListings.filter(isDuplicateCard).map(listing => listing.model) as any;

  // Temporarily passing the PWA search listings response to the shared UI component to avoid duplicating the
  // request for search listings on SRP. Once we migrate the lodging-pwa-property-search.graphql query to shared UI
  // this will be cleaned up.
  // The SRP Listings team is actively working on migrating the search query to shared UI!
  return (
    <PropertyListing
      inputs={propertyListingInput}
      propertySearchListingsPwa={searchListings}
      isLoadingPwa={searchResults.isLoading}
      openDatePicker={openDatepicker}
      onClickCallback={model => {
        currentHotel.setPropertyListingAsCurrentHotel(undefined, model);
      }}
    />
  );
});
